<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="small">
    <template #content>
      <!-- Social network picker -->
      <div
        :class="{
          'social-network-picker': true,
          'social-network-picker--disabled': isSaving || isAddingAccount,
        }"
      >
        <div class="standard-page-header standard-page-header--small-bottom-padding social-network-picker__header" style="margin-bottom: 20px">
          <h1 class="global-h1 standard-page-header__title">{{ !props.allCustomerSocialChannels.length ? "Add Social Accounts" : "Post to Accounts" }}</h1>
        </div>

        <div v-if="!props.allCustomerSocialChannels.length">
          <p class="global-p" style="margin-bottom: 15px">Link your social accounts to start posting!</p>
        </div>
        <div v-else class="social-network-picker__list">
          <SrpCheckbox class="social-network-picker__checkbox" v-for="socialNetwork in props.allCustomerSocialChannels" :key="socialNetwork" size="medium">
            <template #input><input type="checkbox" v-model="_selectedSocialChannels" :value="socialNetwork" /></template>
            <template #text>
              <!-- Icon and name snippet -->
              <div class="icon-and-name-snippet">
                <img class="icon-and-name-snippet__icon" :src="`/images/social-logo-${socialNetwork.toLowerCase()}.svg`" :alt="capitalizeSocialNetworkName(socialNetwork)" />

                <span class="icon-and-name-snippet__name">
                  {{ capitalizeSocialNetworkName(socialNetwork) }}
                </span>
              </div>
              <!-- / Icon and name snippet -->
            </template>
          </SrpCheckbox>
        </div>
        <!-- / Social network picker -->

        <NoteWithIcon v-if="props.allCustomerSocialChannels.length && _selectedSocialChannels.length === 0" class="social-network-picker__note-with-icon" style="margin-bottom: -20px">
          <template #text>
            <h6 class="global-h6">At least one account must be selected.</h6>
          </template>
        </NoteWithIcon>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <SrpButton @click="handleAddSocialAccount" :fill="props.allCustomerSocialChannels.length ? 'outlined' : 'solid'" :isDisabled="isAddingAccount">
        <template #icon>
          <LoadingIcon v-if="isAddingAccount" :size="22" />
          <IconEmbedded v-else name="plus_2-5" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Add Accounts
      </SrpButton>

      <SrpButton
        v-if="props.allCustomerSocialChannels.length"
        @click="
          async () => {
            await save();
            closeModal();
          }
        "
        :isDisabled="isSaving || _selectedSocialChannels.length === 0"
      >
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="22" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { capitalizeSocialNetworkName } from "@contracts/socialNetworks";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Types
import { SocialChannels } from "@contracts/socialNetworks";
import { SocialMediaPostData } from "@contracts/shareStop";
import NoteWithIcon from "@components/NoteWithIcon.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    post: SocialMediaPostData;
    allCustomerSocialChannels: Array<SocialChannels>;
    selectedSocialChannels: Array<SocialChannels> | null;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "save", selectedSocialChannels: Array<SocialChannels>): void;
  (e: "addSocialAccount"): void;
}>();

// Checkbox values ============================================================
const _selectedSocialChannels = ref<Array<SocialChannels>>([]);

onMounted(() => {
  _selectedSocialChannels.value = [...(props.selectedSocialChannels || [])];
});

// Add social account =========================================================
const isAddingAccount = ref<boolean>(false);

async function handleAddSocialAccount() {
  isAddingAccount.value = true;
  emit("addSocialAccount");
}

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  isSaving.value = true;
  const updatedPost = await ShareStopSummaryRepo.updatePost({
    ...props.post,
    postToAccounts: _selectedSocialChannels.value,
  });
  isSaving.value = false;

  emit("save", updatedPost.postToAccounts);
}
</script>

<style scoped lang="scss">
// Icon and name snippet ======================================================
.icon-and-name-snippet {
  display: flex;
  align-items: center;

  &__icon {
    width: 23px;
    margin-right: 5px;
  }

  &__name {
  }
}

// Social network picker ======================================================
.social-network-picker {
  &__header {
  }

  &__list {
    gap: 7px;
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__checkbox {
  }

  &__note-with-icon {
  }
}
</style>
